import { useRef } from "react";
import { Anchor, Center, Paper, Stack, Text } from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import Autoplay from "embla-carousel-autoplay";
import { IconUsers } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import type { EcosystemHeadNavState } from "@/pages/ecosystem-heads/ecosystemHeads.const";
import type { SectorEcosystemOverview } from "../../../../../../shared/sectorEcosystemOverview";
import SectorDashboardCard from "./SectorDashboardCard";
import SectorEcosystemCorridorCard from "./SectorEcosystemCorridorCard";

function EcosystemCorridorComingSoonContent() {
  return (
    <Paper
      p="xl"
      radius="md"
      style={{
        border: "1px dashed #CBD5E1",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Stack gap="sm" align="center" py="md">
        <Center
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor: "#EFF6FF",
          }}
        >
          <IconUsers size={20} color="#2563EB" />
        </Center>
        <Text
          size="xs"
          fw={600}
          c="#2563EB"
          px={12}
          py={4}
          style={{ borderRadius: 999, backgroundColor: "#EFF6FF" }}
        >
          Coming Soon
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          We are launching soon...
        </Text>
      </Stack>
    </Paper>
  );
}

type SectorEcosystemHeadsCarouselProps = {
  overview: SectorEcosystemOverview;
  headsHref: string;
  headsNavState?: EcosystemHeadNavState;
};

const AUTOPLAY_DELAY_MS = 4500;

export default function SectorEcosystemHeadsCarousel({
  overview,
  headsHref,
  headsNavState,
}: SectorEcosystemHeadsCarouselProps) {
  const corridors = overview.ecosystemHeadCorridors;
  const multi = corridors.length > 1;

  const autoplay = useRef(
    Autoplay({
      delay: AUTOPLAY_DELAY_MS,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    }),
  );

  return (
    <SectorDashboardCard
      fillHeight={false}
      title="Ecosystem corridor"
      subtitle="Leaders driving growth in this ecosystem."
      headerAction={
        corridors.length > 0 ? (
          <Anchor component={Link} to={headsHref} state={headsNavState} size="xs" fw={600} c="primary.7">
            View all ecosystems →
          </Anchor>
        ) : undefined
      }
    >
      {corridors.length === 0 ? (
        <EcosystemCorridorComingSoonContent />
      ) : (
        <Carousel
          slideSize={{ base: "85%", sm: "55%", md: "42%" }}
          slideGap="md"
          align="start"
          slidesToScroll={1}
          withControls={false}
          loop={multi}
          plugins={multi ? [autoplay.current] : []}
          onMouseEnter={multi ? autoplay.current.stop : undefined}
          onMouseLeave={multi ? autoplay.current.reset : undefined}
          styles={{
            slide: { height: "auto", minHeight: 188 },
          }}
        >
          {corridors.map((corridor) => (
            <Carousel.Slide
              key={`${corridor.ecosystemHeadUserId}-${corridor.country}-${corridor.targetCountry}`}
            >
              <SectorEcosystemCorridorCard corridor={corridor} navState={headsNavState} />
            </Carousel.Slide>
          ))}
        </Carousel>
      )}
    </SectorDashboardCard>
  );
}
